import React, { useEffect, useMemo, useState } from "react";
import "./ClientCustomer.css";
import Header from "../header/Header";
import Sidebar from "../Sidebar/Sidebar";
import { decryptObject, hasPermission } from "../GlobalFunction";
import {
  getClientCutomerData,
  getClientCutomerDataById,
  updateClientCutomerData,
  getClientCustomerAttachments, // <-- create this API method
  addClientCutomerData,
  getAllCountries,
  getClientFormData,
  getAcountOwnerList,
} from "../../services/ApiService";
import CustomerCountryFeesModal from "./CustomerCountryFeesModal";
import {
  Button,
  Col,
  Empty,
  Form,
  Input,
  message,
  Modal,
  Row,
  Select,
  Spin,
  Tag,
  Tooltip,
} from "antd";
import ServiceUpdatForm from "./Updateform.json";
import { NumericFormat } from "react-number-format";
import {
  ClearOutlined,
  DownOutlined,
  PlusOutlined,
  SearchOutlined,
  UpOutlined,
} from "@ant-design/icons";

const ClientCustomer = () => {
  const canCreateCustomer = hasPermission("customer_list", "create");
  const canUpdateCustomer = hasPermission("customer_list", "update");
  const [AllData, setAllData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [UpdateModal, setUpdateModal] = useState(false);
  const [form] = Form.useForm();
  const [currentClient, setCurrentClient] = useState(null);
  const [modalLoader, setmodalLoader] = useState(false);

  const [isProofModalVisible, setIsProofModalVisible] = useState(false);
  const [proofFile, setProofFile] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [isAttachmentsModalVisible, setIsAttachmentsModalVisible] = useState(false);

  const [formValues, setFormValues] = useState({});
  const [searchText, setSearchText] = useState("");
  const [ownerFilter, setOwnerFilter] = useState(undefined);
  const [expandedSections, setExpandedSections] = useState({});
  const [countryOptions, setCountryOptions] = useState([]);
  const [accountOwnerOptions, setAccountOwnerOptions] = useState([]);
  // The customer whose Country Fees & Account Owners modal is open, or
  // null -- clicking a customer row opens this one modal, which now
  // covers both fees and account-owner management (confirmed with the
  // user, 2026-10-27 -- previously a second "Change Account Owner"
  // button/modal, folded in here instead).
  const [countryFeesCustomer, setCountryFeesCustomer] = useState(null);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [addModalLoader, setAddModalLoader] = useState(false);
  const [addTargetClient, setAddTargetClient] = useState(null);
  const [newCustomerName, setNewCustomerName] = useState("");
  const [newCustomerCountry, setNewCustomerCountry] = useState(undefined);
  const [newCustomerIndustry, setNewCustomerIndustry] = useState("");
  const [newCustomerWebsite, setNewCustomerWebsite] = useState("");
  const [newCustomerCharges, setNewCustomerCharges] = useState({});

  const groupedClients = useMemo(() => {
    const map = new Map();
    AllData.forEach((record) => {
      const clientId = record.client_id;
      if (!map.has(clientId)) {
        map.set(clientId, {
          client_id: clientId,
          client_name: record.client_name || "Unnamed Client",
          customers: [],
        });
      }
      map.get(clientId).customers.push(record);
    });
    return Array.from(map.values());
  }, [AllData]);

  const filteredClients = useMemo(() => {
    const query = searchText.toLowerCase();
    if (!query && !ownerFilter) {
      return groupedClients.map((client) => ({ ...client, forceOpen: false }));
    }

    const results = [];

    groupedClients.forEach((client) => {
      const clientNameMatches = !!query && client.client_name.toLowerCase().includes(query);

      // A customer shows if it passes BOTH the text search (client name
      // match shows every customer under it, or the customer's own name
      // matches) and the account-owner filter, confirmed with the user,
      // 2026-10-22.
      const matchingCustomers = client.customers.filter((customer) => {
        const textMatches =
          !query ||
          clientNameMatches ||
          (customer.client_customer_name || "").toLowerCase().includes(query);
        const ownerMatches = !ownerFilter || (customer.account_owners || []).includes(ownerFilter);
        return textMatches && ownerMatches;
      });

      if (matchingCustomers.length > 0) {
        // Auto-expand whenever the filters actually narrowed this
        // client down to a subset of its customers, so the matches
        // aren't hidden inside a collapsed section.
        const forceOpen = matchingCustomers.length !== client.customers.length;
        results.push({ ...client, customers: matchingCustomers, forceOpen });
      }
    });

    return results;
  }, [groupedClients, searchText, ownerFilter]);

  const toggleCollapse = (clientId) => {
    setExpandedSections((prev) => ({
      ...prev,
      [clientId]: !prev[clientId],
    }));
  };

  const handleClearFilters = () => {
    setSearchText("");
    setOwnerFilter(undefined);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const data = await getClientCutomerData();
      if (data.responseCode === 200) {
        setAllData(data.data || []);
      } else {
        message.error(data.message);
      }
    } catch (error) {
      console.log(error, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const fetchCountryOptions = async () => {
      try {
        const response = await getAllCountries(1);
        const rawObjects = response?.country_list ?? [];

        const options = rawObjects
          .sort((a, b) => (a.country_name || "").localeCompare(b.country_name || ""))
          .map((item) => {
            const fullString = item.country_name || "";
            const cleanName = fullString.split("(")[0].trim();
            return { label: fullString, value: cleanName };
          });

        setCountryOptions(options);
      } catch (error) {
        console.error("Error fetching country data:", error);
      }
    };

    fetchCountryOptions();
  }, []);

  useEffect(() => {
    const fetchAccountOwners = async () => {
      try {
        const res = await getAcountOwnerList();
        if (res?.responseCode === 200) {
          setAccountOwnerOptions(
            (res?.res || []).map((owner) => ({ label: owner.name, value: owner.id }))
          );
        }
      } catch (error) {
        console.error("Error fetching account owners:", error);
      }
    };
    fetchAccountOwners();
  }, []);

  const handleOpenAddCustomer = async (client) => {
    setAddTargetClient(client);
    setNewCustomerName("");
    setNewCustomerCountry(undefined);
    setNewCustomerIndustry("");
    setNewCustomerWebsite("");
    setNewCustomerCharges({});
    setAddModalVisible(true);

    // Pre-fill charges from the parent client's own charges, since a
    // client's charges apply to their customers by default too.
    try {
      setAddModalLoader(true);
      const res = await getClientFormData(client.client_id);
      if (res?.responseCode === 200 && res?.data) {
        const chargeFieldNames = getAllFormFieldNames();
        const prefilledCharges = {};
        chargeFieldNames.forEach((name) => {
          if (res.data[name] !== undefined && res.data[name] !== null) {
            prefilledCharges[name] = res.data[name];
          }
        });
        setNewCustomerCharges(prefilledCharges);
      }
    } catch (error) {
      console.error("Error fetching client charges:", error);
    } finally {
      setAddModalLoader(false);
    }
  };

  const handleCloseAddCustomer = () => {
    setAddModalVisible(false);
    setAddTargetClient(null);
    setNewCustomerName("");
    setNewCustomerCountry(undefined);
    setNewCustomerIndustry("");
    setNewCustomerWebsite("");
    setNewCustomerCharges({});
  };

  // Countries already taken by an existing customer with this SAME name
  // under this client -- confirmed with the user, 2026-10-27: the
  // country dropdown shouldn't offer a country that would just get
  // rejected by add_client_customer's (name, country) uniqueness check
  // anyway. Unions TWO signals, not just the raw `country` field alone --
  // confirmed real bug, 2026-10-27: `country` is the creation-time column
  // and is barely populated on existing customers (most were created
  // before this field existed), so for an older customer it's blank even
  // though their employees are genuinely all in one real country. That
  // real country lives in `customer_countries` instead (derived from
  // each employee's own Country of Employment -- the same reliable
  // signal used everywhere else in this app), so a country counts as
  // "taken" if it matches EITHER field.
  const takenCountriesForNewCustomerName = new Set(
    (addTargetClient?.customers || [])
      .filter((c) => c.client_customer_name.trim().toLowerCase() === newCustomerName.trim().toLowerCase())
      .flatMap((c) => [...(c.customer_countries || []), c.country].filter(Boolean))
  );
  const availableCountryOptionsForAdd = newCustomerName.trim()
    ? countryOptions.filter((o) => !takenCountriesForNewCustomerName.has(o.value))
    : countryOptions;

  const handleAddCustomerSubmit = async () => {
    if (!newCustomerName.trim()) {
      return message.error("Please enter a customer name.");
    }
    if (!newCustomerCountry) {
      return message.error("Please select a country.");
    }

    setAddModalLoader(true);
    try {
      const res = await addClientCutomerData({
        client_id: addTargetClient?.client_id,
        client_customer_name: newCustomerName.trim(),
        country: newCustomerCountry,
        client_customer_industry: newCustomerIndustry.trim(),
        client_customer_website: newCustomerWebsite.trim(),
        ...newCustomerCharges,
      });

      if (res.responseCode === 200) {
        message.success(res.responseMessage || "Customer added successfully");
        handleCloseAddCustomer();
        fetchData();
      } else {
        message.error(res.responseMessage || "Failed to add customer");
      }
    } catch (error) {
      message.error("Something went wrong while adding the customer");
      console.error("Error adding customer:", error);
    } finally {
      setAddModalLoader(false);
    }
  };

  const handleUpdateClient = async (record) => {
    setUpdateModal(true);
    setmodalLoader(true);
    try {
      const clientData = await getClientCutomerDataById(
        record.client_id,
        record.id
      );
      if (clientData.responseCode === 200) {
        setCurrentClient(clientData.data);
        setFormValues(clientData.data);
      } else {
        message.error(clientData.message);
      }
    } catch (error) {
      message.error("Failed to fetch client data");
    } finally {
      setmodalLoader(false);
    }
  };

  const getAllFormFieldNames = () => {
    const fieldNames = [];

    Object.values(ServiceUpdatForm).forEach((sections) => {
      sections.forEach((block) => {
        const fields = Object.values(block)[0];
        fields.forEach((f) => fieldNames.push(f.name));
      });
    });

    return fieldNames;
  };

  const handleUpdateSubmit = async () => {
    try {
      setmodalLoader(true);
      const formFieldNames = getAllFormFieldNames();

      const hasAtLeastOneValue = formFieldNames.some((field) => {
        const val = formValues[field];
        return val !== "" && val !== null && val !== undefined;
      });

      if (!hasAtLeastOneValue) {
        return message.error(
          "Please fill at least one field before submitting."
        );
      }

      for (let section of Object.values(ServiceUpdatForm)) {
        for (let block of section) {
          const fields = Object.values(block)[0];
          fields.forEach((f) => {
            if (
              f.required &&
              (!formValues[f.name] || formValues[f.name] === "")
            ) {
              return message.error(`${f.label} is required`);
            }
          });
        }
      }

      const body = {
        ...currentClient,
        ...formValues,
      };

      const pricingFields = ["charge", "price", "amount", "fee"];
      let pricingChanged = false;

      for (let key in formValues) {
        if (
          pricingFields.some(pf => key.toLowerCase().includes(pf))
        ) {
          if (String(formValues[key]) !== String(currentClient[key])) {
            pricingChanged = true;
            break;
          }
        }
      }

      if (pricingChanged) {
        setmodalLoader(false);
        setIsProofModalVisible(true);
        return;
      }

      const formDataPayload = new FormData();
      formDataPayload.append("data", JSON.stringify(body));

      const res = await updateClientCutomerData(currentClient.id, formDataPayload);
      if (res.responseCode === 200) {
        message.success(res.responseMessage);
        setUpdateModal(false);
        setCurrentClient(null);
        setFormValues({});
      } else {
        message.error(res.responseMessage || "Update failed");
      }
    } catch (err) {
      console.log("Validation or API error:", err);
    } finally {
      setmodalLoader(false);
    }
  };

  const handleFinalUpdateSubmit = async () => {
    if (!proofFile) {
      return message.error("Please upload the pricing proof document.");
    }
    try {
      setmodalLoader(true);
      const body = {
        ...currentClient,
        ...formValues,
      };
      const formDataPayload = new FormData();
      formDataPayload.append("data", JSON.stringify(body));
      formDataPayload.append("proof_file", proofFile);

      const res = await updateClientCutomerData(currentClient.id, formDataPayload);
      if (res.responseCode === 200) {
        message.success(res.responseMessage);
        setIsProofModalVisible(false);
        setProofFile(null);
        setUpdateModal(false);
        setCurrentClient(null);
        setFormValues({});
      } else {
        message.error(res.responseMessage || "Update failed");
      }
    } catch (err) {
      console.error(err);
      message.error("Something went wrong");
    } finally {
      setmodalLoader(false);
    }
  };

  
const fetchAndShowAttachments = async () => {
  try {
    setmodalLoader(true);
    const res = await getClientCustomerAttachments(currentClient.id);
    if (res.responseCode === 200) {
      setAttachments(res.attachments || []);
      setIsAttachmentsModalVisible(true);
    } else {
      message.error(res.responseMessage || "Failed to fetch attachments");
    }
  } catch (err) {
    console.error(err);
    message.error("Error fetching attachments");
  } finally {
    setmodalLoader(false);
  }
};

  const handlecancelModal = () => {
    setUpdateModal(false);
    setCurrentClient(null);
    setFormValues({});
  };

  const renderField = (fieldName, fieldData, values, onFieldChange) => {
    const value = values[fieldName] ?? "";

    const handleChange = (val) => onFieldChange(fieldName, val);

    return (
      <Form.Item key={fieldName} label={fieldData.label}>
        {fieldData.input_type === "amount" ? (
          <NumericFormat
            className="input-box"
            value={value}
            placeholder={fieldData.placeholder}
            thousandSeparator
            thousandsGroupStyle="lakh"
            decimalScale={2}
            allowNegative={false}
            valueIsNumericString
            customInput={Input}
            onValueChange={(vals) => {
              handleChange(vals.value);
            }}
          />
        ) : (
          <Input
            className="input-box"
            value={value}
            placeholder={fieldData.placeholder}
            type={fieldData.input_type === "number" ? "number" : "text"}
            onChange={(e) => handleChange(e.target.value)}
          />
        )}
      </Form.Item>
    );
  };

  const renderSection = (sectionName, sectionData, values, onFieldChange) => {
    const fields = sectionData.map((field) =>
      renderField(field.name, field, values, onFieldChange)
    );

    return (
      <div key={sectionName} className="Updatesection custom-scrollbar">
        <Row gutter={[10, 10]}>
          {fields.map((field, idx) => (
            <Col span={8} key={idx}>
              {field}
            </Col>
          ))}
        </Row>
      </div>
    );
  };

  // const handleSearch = async () => {
  //   try {
  //   } catch (error) {}
  // };

  return (
    <>
      <div className="main-box">
        <Sidebar activeId={10} />
        <main className="main-content">
          <Header title="Settings" />
          <div style={{ flex: 1, backgroundColor: "#FAFAFB" }}>
            <div className="service-container">
              <div
                style={{
                  position: "sticky",
                  top: 0,
                  zIndex: 10,
                  backgroundColor: "#FAFAFB",
                  display: "flex",
                  alignItems: "center",
                  gap: "1rem",
                  paddingBottom: "1rem",
                }}
              >
                <Input
                  placeholder="Search by client or customer name..."
                  prefix={<SearchOutlined />}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  allowClear
                  style={{ fontSize: "0.8rem", width: "260px" }}
                />
                <Select
                  placeholder="Filter by account owner"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  value={ownerFilter}
                  onChange={(value) => setOwnerFilter(value)}
                  options={accountOwnerOptions}
                  style={{ fontSize: "0.8rem", width: "220px" }}
                />
                {(searchText || ownerFilter) && (
                  <Button icon={<ClearOutlined />} onClick={handleClearFilters}>
                    Clear Filters
                  </Button>
                )}
              </div>

              <Spin spinning={loading} size="large" tip="Loading...">
                <div className="client-group-container custom-scrollbar">
                  {filteredClients.length === 0 ? (
                    <Empty description="No clients found" />
                  ) : (
                    filteredClients.map((client) => {
                      const isExpanded =
                        client.forceOpen || !!expandedSections[client.client_id];
                      return (
                      <div key={client.client_id} className="client-group">
                        <div
                          className="client-group-header"
                          onClick={() => toggleCollapse(client.client_id)}
                        >
                          <strong>{client.client_name}</strong>{" "}
                          ({client.customers.length})
                          <span className="toggle-arrow">
                            {isExpanded ? (
                              <UpOutlined />
                            ) : (
                              <DownOutlined />
                            )}
                          </span>
                        </div>

                        {isExpanded && (
                          <div className="customer-list">
                            {canCreateCustomer && (
                              <Button
                                type="dashed"
                                icon={<PlusOutlined />}
                                className="add-customer-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenAddCustomer(client);
                                }}
                              >
                                Add Customer
                              </Button>
                            )}
                            <div className="customer-row customer-row-header">
                              <span className="customer-name">Customer Name</span>
                              <span className="customer-country">Country</span>
                              <span className="customer-account-owner">Account Owner</span>
                            </div>
                            {client.customers.map((customer) => (
                              <div
                                key={customer.id}
                                className="customer-row"
                                onClick={
                                  canUpdateCustomer
                                    ? () => setCountryFeesCustomer(customer)
                                    : undefined
                                }
                                style={canUpdateCustomer ? undefined : { cursor: "default" }}
                              >
                                <span className="customer-name">
                                  {customer.client_customer_name}
                                </span>
                                <span className="customer-country">
                                  {(() => {
                                    const countries = customer.customer_countries || [];
                                    if (countries.length === 0) return "-";
                                    const shown = countries.slice(0, 2).join(", ");
                                    const extra = countries.length - 2;
                                    return extra > 0 ? (
                                      <Tooltip title={countries.join(", ")}>
                                        {shown} <Tag>+{extra}</Tag>
                                      </Tooltip>
                                    ) : (
                                      shown
                                    );
                                  })()}
                                </span>
                                <span className="customer-account-owner">
                                  {(() => {
                                    const owners = customer.account_owners || [];
                                    if (owners.length === 0) return "Unassigned";
                                    const labels = owners.map(
                                      (id) => accountOwnerOptions.find((o) => o.value === id)?.label || id
                                    );
                                    const shown = labels.slice(0, 2).join(", ");
                                    const extra = labels.length - 2;
                                    return extra > 0 ? (
                                      <Tooltip title={labels.join(", ")}>
                                        {shown} <Tag>+{extra}</Tag>
                                      </Tooltip>
                                    ) : (
                                      shown
                                    );
                                  })()}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      );
                    })
                  )}
                </div>
              </Spin>
            </div>
          </div>
        </main>
      </div>

      <Modal
        title={currentClient?.client_customer_name}
        open={UpdateModal}
        onCancel={handlecancelModal}
        footer={null}
        centered={true}
        width={800}
      >
        <Spin spinning={modalLoader} size="large" tip="Loading...">
          <Form form={form} layout="vertical">
            {Object.keys(ServiceUpdatForm).map((stepKey) => {
              const stepData = ServiceUpdatForm[stepKey];
              if (!stepData || !Array.isArray(stepData)) return null;
              return stepData.map((sectionData) => {
                const sectionName = Object.keys(sectionData)[0];
                return renderSection(
                  sectionName,
                  sectionData[sectionName],
                  formValues,
                  (name, value) =>
                    setFormValues((prev) => ({ ...prev, [name]: value }))
                );
              });
            })}
          </Form>

          <div className="button-container">
            
              <Button
                type="dashed"
                onClick={fetchAndShowAttachments}
                style={{ marginRight: "1rem", color: "#1890ff", borderColor: "#1890ff" }}
              >
                ?? View Attachments
              </Button>
              <Button type="primary" onClick={handleUpdateSubmit}>

              Update Details
            </Button>
          </div>
        </Spin>
      </Modal>

      <Modal
        title={`Add Customer${
          addTargetClient?.client_name ? ` — ${addTargetClient.client_name}` : ""
        }`}
        open={addModalVisible}
        onCancel={handleCloseAddCustomer}
        footer={null}
        centered
        width={800}
      >
        <Spin spinning={addModalLoader} size="large" tip="Adding...">
          <Form layout="vertical">
            <Row gutter={[10, 10]}>
              <Col span={8}>
                <Form.Item label="Customer Name" required>
                  <Input
                    className="input-box"
                    placeholder="Enter customer name"
                    value={newCustomerName}
                    onChange={(e) => {
                      const value = e.target.value;
                      setNewCustomerName(value);
                      // Clear an already-picked country if it's no longer
                      // valid for the newly-typed name (e.g. it just
                      // became a duplicate match), so a stale selection
                      // can't silently slip through on submit.
                      const stillTaken = (addTargetClient?.customers || []).some(
                        (c) =>
                          c.client_customer_name.trim().toLowerCase() === value.trim().toLowerCase() &&
                          [...(c.customer_countries || []), c.country].filter(Boolean).includes(newCustomerCountry)
                      );
                      if (newCustomerCountry && stillTaken) {
                        setNewCustomerCountry(undefined);
                      }
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Country" required>
                  <Select
                    showSearch
                    allowClear
                    placeholder="Select country"
                    value={newCustomerCountry}
                    onChange={(value) => setNewCustomerCountry(value)}
                    options={availableCountryOptionsForAdd}
                    optionFilterProp="label"
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Industry">
                  <Input
                    className="input-box"
                    placeholder="Enter customer industry"
                    value={newCustomerIndustry}
                    onChange={(e) => setNewCustomerIndustry(e.target.value)}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Website">
                  <Input
                    className="input-box"
                    placeholder="Enter customer website"
                    value={newCustomerWebsite}
                    onChange={(e) => setNewCustomerWebsite(e.target.value)}
                  />
                </Form.Item>
              </Col>
            </Row>

            {Object.keys(ServiceUpdatForm).map((stepKey) => {
              const stepData = ServiceUpdatForm[stepKey];
              if (!stepData || !Array.isArray(stepData)) return null;
              return stepData.map((sectionData) => {
                const sectionName = Object.keys(sectionData)[0];
                return renderSection(
                  sectionName,
                  sectionData[sectionName],
                  newCustomerCharges,
                  (name, value) =>
                    setNewCustomerCharges((prev) => ({ ...prev, [name]: value }))
                );
              });
            })}
          </Form>

          <div className="button-container">
            <Button type="primary" onClick={handleAddCustomerSubmit}>
              Add Customer
            </Button>
          </div>
        </Spin>
      </Modal>

      <Modal
        title="Upload Pricing Proof"
        open={isProofModalVisible}
        onOk={handleFinalUpdateSubmit}
        onCancel={() => {
          setIsProofModalVisible(false);
          setProofFile(null);
        }}
        okText="Submit Update"
        centered
      >
        <p>You have changed one or more pricing fields. Please upload a supporting document (e.g., SOW or Pricing Addendum).</p>
        <Input
          type="file"
          accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
          onChange={(e) => setProofFile(e.target.files[0])}
        />
      </Modal>

      <Modal
        title="Client Customer Attachments"
        open={isAttachmentsModalVisible}
        onCancel={() => setIsAttachmentsModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsAttachmentsModalVisible(false)}>
            Close
          </Button>,
        ]}
        centered
      >
        {attachments.length > 0 ? (
          <ul style={{ listStyleType: "none", padding: 0 }}>
            {attachments.map((file, idx) => (
              <li key={idx} style={{ marginBottom: "1rem", padding: "0.5rem", border: "1px solid #eee", borderRadius: "5px" }}>
                <strong>{file.type}: </strong>
                <a href={file.url} target="_blank" rel="noopener noreferrer" style={{ color: "#1890ff" }}>
                  {file.name}
                </a>
              </li>
            ))}
          </ul>
        ) : (
          <Empty description="No attachments found for this customer." />
        )}
      </Modal>

      <CustomerCountryFeesModal
        open={!!countryFeesCustomer}
        customer={countryFeesCustomer}
        canEdit={canUpdateCustomer}
        accountOwnerOptions={accountOwnerOptions}
        onClose={() => setCountryFeesCustomer(null)}
        onOverallOwnerSaved={() => fetchData()}
      />
    </>
  );
};

export default ClientCustomer;
